import { useMemo, useState } from "react";
import { AlertTriangle, Check, Plus } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/design-system/lib/utils";
import { extractErrorMessage } from "@/api/client";
import { useSpvPoolMath } from "@/hooks/capitalMarkets/useSpvPools";
import {
  useCreateCommitment,
  useInvestorList,
} from "@/hooks/capitalMarkets/useInvestors";
import {
  useEntityNameMap,
  usePersonNameMap,
  resolveInvestorName,
} from "@/hooks/capitalMarkets/useCounterpartyNames";
import type { InvestorMaskedDto } from "@/types/investors";
import { NewInvestorModal } from "../../investors/NewInvestorModal";
import { formatCurrency } from "../../shared/format";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface WizardProps {
  poolId: string;
  poolName: string;
  onClose: () => void;
  /** Fired after a commitment is created so the host can refresh its table. */
  onComplete: () => void;
}

type Step = 1 | 2 | 3;

export function InviteInvestorWizard({
  poolId,
  poolName,
  onClose,
  onComplete,
}: WizardProps) {
  const toast = useToast();
  const tz = useUserTimeZone();
  const [step, setStep] = useState<Step>(1);

  // --- component-local wizard state (never URL / localStorage) -------------
  const [investorId, setInvestorId] = useState("");
  const [trancheId, setTrancheId] = useState("");
  const [amount, setAmount] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const investorsQuery = useInvestorList({ pageSize: 200 });
  const mathQuery = useSpvPoolMath(poolId);
  const { map: personNames } = usePersonNameMap();
  const { map: entityNames } = useEntityNameMap();
  const createCommitment = useCreateCommitment();

  const investors = useMemo(
    () => investorsQuery.data?.data ?? [],
    [investorsQuery.data],
  );
  const nameOf = (inv: InvestorMaskedDto) =>
    resolveInvestorName(inv.personId, inv.entityId, personNames, entityNames);

  const filteredInvestors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return investors;
    return investors.filter((i) => nameOf(i).toLowerCase().includes(term));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investors, search, personNames, entityNames]);

  const selectedInvestor = investors.find((i) => i.id === investorId) ?? null;
  const tranches = mathQuery.data?.perTrancheFill ?? [];
  const selectedTranche = tranches.find((t) => t.poolTrancheId === trancheId) ?? null;

  const amountNum = Number(amount);
  const remaining = selectedTranche
    ? selectedTranche.targetAmount - selectedTranche.committedAmount
    : 0;
  const exceedsTranche =
    selectedTranche !== null && amountNum > remaining && amountNum > 0;

  const step1Valid =
    selectedInvestor !== null && selectedInvestor.status === "Active";
  const step2Valid =
    selectedTranche !== null && Number.isFinite(amountNum) && amountNum > 0;

  const handleGenerate = () => {
    if (!selectedInvestor || !selectedTranche) return;
    createCommitment
      .mutateAsync({
        investorId: selectedInvestor.id,
        spvPoolId: poolId,
        poolTrancheId: selectedTranche.poolTrancheId,
        committedAmount: amountNum,
        fundedAmount: 0,
        commitmentDate: isoDateInTz(new Date(), tz),
        status: "Indication",
      })
      .then(() => {
        toast.success("Investor commitment created as an Indication.");
        onComplete();
        onClose();
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Invite Investor"
      description={`Pool: ${poolName}`}
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex items-center gap-space-2">
            {step > 1 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setStep((s) => (s - 1) as Step)}
              >
                Back
              </Button>
            )}
            {step < 3 && (
              <Button
                size="sm"
                disabled={step === 1 ? !step1Valid : !step2Valid}
                onClick={() => setStep((s) => (s + 1) as Step)}
              >
                Next
              </Button>
            )}
            {step === 3 && (
              <Button
                size="sm"
                loading={createCommitment.isPending}
                onClick={handleGenerate}
              >
                Generate subscription package
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Stepper step={step} />

      {step === 1 && (
        <div className="flex flex-col gap-space-3">
          <FormField label="Search existing investors">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type a name…"
              aria-label="Search investors"
            />
          </FormField>
          <div className="max-h-64 overflow-y-auto border border-border-light rounded-md divide-y divide-border-light">
            {filteredInvestors.length === 0 ? (
              <p className="text-caption text-gray-mid italic p-space-3 m-0">
                No matching investors.
              </p>
            ) : (
              filteredInvestors.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => setInvestorId(inv.id)}
                  className={cn(
                    "w-full text-left px-space-3 py-space-2 flex items-center justify-between",
                    inv.id === investorId
                      ? "bg-gold/10"
                      : "hover:bg-white-warm",
                  )}
                >
                  <span className="text-small">{nameOf(inv)}</span>
                  <span className="text-caption text-gray-mid">
                    {inv.status}
                  </span>
                </button>
              ))
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} aria-hidden /> Create new investor
          </Button>
          {selectedInvestor && selectedInvestor.status !== "Active" && (
            <WarningBanner>
              {nameOf(selectedInvestor)} is {selectedInvestor.status}. Only
              Active investors can be invited to a pool.
            </WarningBanner>
          )}
          {showCreate && (
            <NewInvestorModal
              onClose={() => setShowCreate(false)}
              onCreated={(id) => {
                setShowCreate(false);
                setInvestorId(id);
              }}
            />
          )}
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-space-3">
          <p className="text-small text-gray-mid m-0">
            Select a tranche and enter this investor&apos;s commitment amount.
          </p>
          <div className="flex flex-col gap-space-2">
            {tranches.length === 0 && (
              <p className="text-caption text-gray-mid italic m-0">
                This pool has no tranches configured yet.
              </p>
            )}
            {tranches.map((t) => {
              const rem = t.targetAmount - t.committedAmount;
              const full = rem <= 0;
              return (
                <button
                  key={t.poolTrancheId}
                  type="button"
                  disabled={full}
                  onClick={() => setTrancheId(t.poolTrancheId)}
                  className={cn(
                    "text-left px-space-3 py-space-2 rounded-md border",
                    full && "opacity-50 cursor-not-allowed",
                    t.poolTrancheId === trancheId
                      ? "border-gold bg-gold/10"
                      : "border-border-light hover:border-gold",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-small">
                      Tier {t.trancheCode}
                    </span>
                    <span className="text-caption text-gray-mid">
                      {full
                        ? "Filled"
                        : `${formatCurrency(rem)} of ${formatCurrency(t.targetAmount)} remaining`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <FormField label="Commitment amount (USD)" required>
            <Input
              type="number"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              aria-label="Commitment amount"
            />
          </FormField>
          {exceedsTranche && (
            <WarningBanner>
              This commitment exceeds the tranche&apos;s remaining capacity of{" "}
              {formatCurrency(remaining)}.
            </WarningBanner>
          )}
          {step2Valid && (
            <WarningBanner tone="info">
              Investor concentration limits are not auto-checked in this
              release — confirm this commitment against the pool&apos;s
              investor concentration limit before closing.
            </WarningBanner>
          )}
        </div>
      )}

      {step === 3 && selectedInvestor && selectedTranche && (
        <div className="flex flex-col gap-space-3">
          <h4 className="font-display font-bold text-small m-0">
            Review &amp; generate
          </h4>
          <dl className="grid grid-cols-2 gap-space-2 text-small m-0">
            <dt className="text-gray-mid">Investor</dt>
            <dd className="m-0">{nameOf(selectedInvestor)}</dd>
            <dt className="text-gray-mid">Tranche</dt>
            <dd className="m-0">Tier {selectedTranche.trancheCode}</dd>
            <dt className="text-gray-mid">Commitment amount</dt>
            <dd className="m-0">{formatCurrency(amountNum)}</dd>
          </dl>
          <p className="text-caption text-gray-mid m-0">
            Generating the subscription package creates an{" "}
            <strong>Indication</strong> commitment for this investor. Full
            subscription-document generation via LLM &amp; Reports lands in a
            later release.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ["Pick investor", "Tranche & amount", "Generate package"];
  return (
    <ol className="flex items-center gap-space-2 mb-space-4">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        const done = step > n;
        const active = step === n;
        return (
          <li key={label} className="flex items-center gap-space-2">
            <span
              className={cn(
                "h-6 w-6 rounded-full flex items-center justify-center text-caption font-bold",
                done && "bg-status-success text-white-pure",
                active && "bg-gold text-onyx",
                !done && !active && "bg-white-warm text-gray-mid",
              )}
            >
              {done ? <Check size={12} aria-hidden /> : n}
            </span>
            <span
              className={cn(
                "text-caption font-display uppercase tracking-wide",
                active ? "text-onyx" : "text-gray-mid",
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function WarningBanner({
  children,
  tone = "warning",
}: {
  children: React.ReactNode;
  tone?: "warning" | "info";
}) {
  return (
    <div
      className={cn(
        "flex gap-space-2 rounded-md p-space-3 text-caption",
        tone === "warning"
          ? "bg-status-warning/10 border border-status-warning/30"
          : "bg-status-info/10 border border-status-info/30",
      )}
    >
      <AlertTriangle
        size={14}
        className={cn(
          "shrink-0 mt-[2px]",
          tone === "warning" ? "text-status-warning" : "text-status-info",
        )}
        aria-hidden
      />
      <span>{children}</span>
    </div>
  );
}
